// src/components/FriendTile.js
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useHref, useNavigate } from 'react-router-dom';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import WorkOutlineRoundedIcon from '@mui/icons-material/WorkOutlineRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import pb from '../lib/pb';
import { getMediaUrl, getPbUrl } from '../lib/media';
import { injectStyles } from '../shared/PostCard';

/* ------------------------------------------------------------------ */
/* Shared helpers (also used by FriendsPanel and Profile)              */
/* ------------------------------------------------------------------ */
export const userName = (u) => (u?.name && String(u.name).trim()) || u?.username || 'Member';

/**
 * Every URL a file of a record could be served from, best guess first.
 * (handles single files, multi-file fields, absolute URLs, SDK differences and the CDN helper)
 */
export function fileUrlCandidates(record, filename) {
  const file = Array.isArray(filename) ? filename[0] : filename;
  if (!record || !file || typeof file !== 'string') return [];
  if (/^https?:\/\//i.test(file)) return [file];
  const list = [];
  const add = (fn) => {
    try {
      const u = fn();
      if (u && typeof u === 'string' && !list.includes(u)) list.push(u);
    } catch { /* try the next one */ }
  };
  add(() => (pb.files.getURL || pb.files.getUrl).call(pb.files, record, file));
  add(() => getMediaUrl(record, file));
  add(() => getPbUrl(record, file));
  add(() => `${pb.baseUrl}/api/files/${record.collectionId || record.collectionName || 'users'}/${record.id}/${file}`);
  return list;
}

export const safeHref = (url) => {
  const u = String(url || '').trim();
  if (!u) return null;
  const full = /^https?:\/\//i.test(u) ? u : `https://${u}`;
  try {
    const p = new URL(full);
    return p.protocol === 'http:' || p.protocol === 'https:' ? p.href : null;
  } catch {
    return null;
  }
};

/** linkLabels can be an array of {label,url}, an array of strings or a JSON string */
export function normalizeLinks(raw) {
  let arr = raw;
  if (typeof arr === 'string') {
    try { arr = JSON.parse(arr); } catch { arr = []; }
  }
  if (!Array.isArray(arr)) return [];
  return arr
    .map((l) => (typeof l === 'string' ? { label: '', url: l } : { label: l?.label || '', url: l?.url || '' }))
    .map((l) => ({ ...l, href: safeHref(l.url) }))
    .filter((l) => l.href)
    .map((l) => ({ ...l, label: l.label || new URL(l.href).hostname.replace(/^www\./, '') }))
    .slice(0, 5);
}

export const normalizeSkills = (raw) => {
  if (!raw) return [];
  let arr = raw;
  if (typeof arr === 'string') {
    try { const j = JSON.parse(arr); if (Array.isArray(j)) arr = j; } catch { /* plain text */ }
  }
  arr = Array.isArray(arr) ? arr : String(arr).split(',');
  return arr.map((s) => String(s).trim()).filter(Boolean);
};

const hueOf = (s) => {
  let h = 0;
  for (const c of String(s)) h = (h * 31 + c.charCodeAt(0)) % 360;
  return h;
};

/** <img> that walks through all candidate URLs before giving up (renders nothing if all fail) */
export function FileImg({ urls, alt = '', className, style }) {
  const [idx, setIdx] = useState(0);
  const key = urls.join('|');
  useEffect(() => { setIdx(0); }, [key]);
  if (!urls.length || idx >= urls.length) return null;
  return (
    <img
      className={className}
      style={style}
      src={urls[idx]}
      alt={alt}
      loading="lazy"
      onError={() => setIdx((i) => i + 1)}
    />
  );
}

/** Avatar. Tries every URL of the file, then falls back to a coloured initial. */
export function FriendAvatar({ user, size = 44, radius = '50%' }) {
  const urls = useMemo(
    () => fileUrlCandidates(user, user?.avatar),
    [user?.id, user?.avatar, user?.collectionId],
  );
  const [idx, setIdx] = useState(0);
  const key = urls.join('|');
  useEffect(() => { setIdx(0); }, [key]);
  const name = userName(user);

  if (!urls.length || idx >= urls.length) {
    const h = hueOf(name);
    return (
      <span
        className="ft-ava ft-ava--fallback"
        style={{
          width: size,
          height: size,
          borderRadius: radius,
          fontSize: Math.round(size * 0.42),
          background: `linear-gradient(135deg,hsl(${h},65%,48%),hsl(${(h + 45) % 360},65%,42%))`,
        }}
        aria-hidden="true"
      >
        {name.trim()[0]?.toUpperCase() || '?'}
      </span>
    );
  }
  return (
    <img
      className="ft-ava"
      src={urls[idx]}
      alt=""
      width={size}
      height={size}
      style={{ width: size, height: size, borderRadius: radius }}
      loading="lazy"
      onError={() => setIdx((i) => i + 1)}
    />
  );
}

const RELATIONS = [
  { value: 'friend', label: 'Friend', icon: '🤝' },
  { value: 'family', label: 'Family', icon: '🏡' },
  { value: 'stranger', label: 'Stranger', icon: '👋' },
];

/* ------------------------------------------------------------------ */
/* Styles — shared tokens + buttons for the friends feature + Profile   */
/* (uses the Feed's --mf-* theme tokens, with dark fallbacks)           */
/* ------------------------------------------------------------------ */
const TILE_CSS = `
.fr,.ft,.fh,.pf{
  --fr-bg:var(--mf-surface,#161b26);
  --fr-bg2:var(--mf-surface-2,#1e2533);
  --fr-bg3:var(--mf-surface-3,#283042);
  --fr-border:var(--mf-border,rgba(255,255,255,.08));
  --fr-border-strong:var(--mf-border-strong,rgba(255,255,255,.16));
  --fr-text:var(--mf-text,#f3f5f9);
  --fr-muted:var(--mf-muted,#aab2c2);
  --fr-faint:var(--mf-faint,#7b8497);
  --fr-accent:var(--mf-accent,#6c8cff);
  --fr-accent-soft:var(--mf-accent-soft,rgba(108,140,255,.16));
  --fr-grad:var(--mf-grad,linear-gradient(135deg,#6c8cff,#b06cff));
  --fr-tab:var(--mf-tab-active,#2a3347);
  --fr-shadow:var(--mf-shadow,0 10px 30px rgba(0,0,0,.28));
  --fr-overlay:var(--mf-overlay,rgba(0,0,0,.55));
  --fr-ok:#16a34a; --fr-danger:#ef4444; --fr-warn:#d97706;
}

/* ---------- buttons ---------- */
.fr-btn{display:inline-flex;align-items:center;justify-content:center;gap:5px;height:34px;padding:0 13px;border-radius:10px;
  border:1px solid var(--fr-border-strong);background:var(--fr-bg3);color:var(--fr-text);font:inherit;font-size:12.5px;font-weight:700;
  cursor:pointer;white-space:nowrap;text-decoration:none;transition:filter .15s,transform .15s,background-color .15s,opacity .15s}
.fr-btn:hover:not(:disabled){filter:brightness(1.12)}
.fr-btn:active:not(:disabled){transform:scale(.97)}
.fr-btn:disabled{opacity:.5;cursor:not-allowed}
.fr-btn svg{font-size:17px}
.fr-btn--icon{width:34px;padding:0}
.fr-btn--primary{background:var(--fr-grad);border-color:transparent;color:#fff;box-shadow:0 4px 12px rgba(0,0,0,.2)}
.fr-btn--ok{background:var(--fr-ok);border-color:transparent;color:#fff}
.fr-btn--ghost{background:transparent}
.fr-btn--danger{background:color-mix(in srgb,var(--fr-danger) 14%,transparent);border-color:color-mix(in srgb,var(--fr-danger) 40%,transparent);color:var(--fr-danger)}
.fr-btn:focus-visible,.fr-chip-btn:focus-visible,.ft-id:focus-visible,.ft-x:focus-visible{outline:2px solid var(--fr-accent);outline-offset:2px}

/* ---------- avatar ---------- */
.ft-ava{display:block;flex:none;object-fit:cover;background:var(--fr-bg3);box-shadow:0 0 0 1px var(--fr-border-strong)}
.ft-ava--fallback{display:grid;place-items:center;color:#fff;font-weight:800}

/* ---------- tile ---------- */
.ft{position:relative;display:flex;flex-direction:column;gap:10px;padding:14px;min-width:0;border-radius:16px;background:var(--fr-bg2);
  border:1px solid var(--fr-border);color:var(--fr-text);touch-action:pan-y;
  transition:transform .18s,opacity .18s,border-color .15s,box-shadow .15s}
.ft:hover{border-color:var(--fr-border-strong);box-shadow:var(--fr-shadow)}
.ft.is-drag{transition:none}
.ft.is-leaving{opacity:0;transform:scale(.96)}
.ft-x{position:absolute;top:8px;right:8px;width:28px;height:28px;border-radius:9px;border:0;background:transparent;color:var(--fr-faint);
  display:grid;place-items:center;cursor:pointer;transition:background-color .15s,color .15s}
.ft-x:hover{background:var(--fr-bg3);color:var(--fr-text)}
.ft-x svg{font-size:18px}
.ft-id{display:flex;align-items:center;gap:12px;min-width:0;padding:0 30px 0 0;border:0;background:none;color:inherit;font:inherit;text-align:left;cursor:pointer}
.ft-meta{display:flex;flex-direction:column;gap:2px;min-width:0}
.ft-name{font-size:15px;font-weight:800;line-height:1.25;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.ft-sub{font-size:12.5px;color:var(--fr-muted);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.ft-stat{font-size:12px;color:var(--fr-faint)}
.ft-bio{margin:0;font-size:13px;line-height:1.45;color:var(--fr-muted);display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;overflow-wrap:anywhere}
.ft-skills{display:flex;flex-wrap:wrap;gap:6px}
.ft-skill{font-size:11px;font-weight:700;padding:3px 9px;border-radius:999px;color:var(--fr-text);line-height:1.3}
.ft-skill--more{background:var(--fr-bg3);border:1px solid var(--fr-border);color:var(--fr-muted)}
.ft-actions{display:flex;flex-direction:column;gap:8px;padding-top:2px}
.ft-rel{display:flex;gap:6px;flex-wrap:wrap}
.fr-chip-btn{display:inline-flex;align-items:center;gap:5px;padding:6px 11px;border-radius:999px;border:1px solid var(--fr-border-strong);
  background:var(--fr-bg);color:var(--fr-muted);font:inherit;font-size:12px;font-weight:700;cursor:pointer;transition:background-color .15s,border-color .15s,color .15s}
.fr-chip-btn:hover{color:var(--fr-text)}
.fr-chip-btn:disabled{opacity:.6;cursor:not-allowed}
.fr-chip-btn.is-on{background:var(--fr-accent-soft);border-color:var(--fr-accent);color:var(--fr-accent)}
.ft-btns{display:flex;gap:8px}
.ft-btns .fr-btn--primary,.ft-btns .fr-btn--ok{flex:1}
.ft--compact{padding:12px;gap:8px}

/* ---------- hover profile card (portaled into the Feed root, floats over everything) ---------- */
.fh{position:fixed;z-index:3000;width:min(600px,calc(100vw - 16px));max-height:calc(100vh - 16px);overflow-y:auto;visibility:hidden;
  background:var(--fr-bg);color:var(--fr-text);border:1px solid var(--fr-border-strong);border-radius:20px;box-shadow:0 24px 60px rgba(0,0,0,.45);
  animation:fh-in .16s ease-out}
.fh::before,.fh::after{content:'';position:absolute;top:0;bottom:0;width:14px}
.fh::before{left:-14px}.fh::after{right:-14px}
@keyframes fh-in{from{opacity:0;transform:translateY(6px) scale(.985)}to{opacity:1;transform:none}}
.fh-body{display:grid;grid-template-columns:112px minmax(0,1fr);gap:16px;padding:16px 16px 14px}
.fh-left{display:flex;flex-direction:column;align-items:center;gap:8px;text-align:center}
.fh-left strong{font-size:18px;line-height:1}
.fh-left span{font-size:11.5px;color:var(--fr-faint)}
.fh-right{min-width:0;display:flex;flex-direction:column}
.fh-cover{position:relative;height:118px;border-radius:14px;overflow:hidden;background:var(--fr-bg3)}
.fh-cover img,.fh-cover video{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block}
.fh-details{display:flex;flex-direction:column;gap:7px;margin-top:12px;min-width:0}
.fh-name{margin:0;font-size:18px;font-weight:800;line-height:1.2;overflow-wrap:anywhere}
.fh-handle{font-size:13px;color:var(--fr-muted)}
.fh-meta{display:flex;flex-wrap:wrap;gap:6px 12px;font-size:12.5px;color:var(--fr-muted)}
.fh-meta span{display:inline-flex;align-items:center;gap:4px}
.fh-meta svg{font-size:15px;color:var(--fr-faint)}
.fh-bio{margin:0;font-size:13.5px;line-height:1.5;color:var(--fr-text);overflow-wrap:anywhere;white-space:pre-wrap}
.fh-links{display:flex;flex-wrap:wrap;gap:6px}
.fh-link{display:inline-flex;align-items:center;gap:4px;font-size:12px;font-weight:700;padding:4px 10px;border-radius:999px;color:var(--fr-accent);
  background:var(--fr-accent-soft);text-decoration:none;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.fh-link svg{font-size:14px}
.fh-foot{display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;padding:12px 16px;border-top:1px solid var(--fr-border);background:var(--fr-bg2)}
.fh-req{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.fh-go{margin-left:auto}
@media (max-width:520px){.fh-body{grid-template-columns:1fr}.fh-left{flex-direction:row;justify-content:flex-start;text-align:left}}
@media (prefers-reduced-motion:reduce){.ft,.fr-btn,.fh{transition:none!important;animation:none!important}}
`;
injectStyles('mf-friends-shared-css', TILE_CSS);

/* ------------------------------------------------------------------ */
/* Pieces                                                              */
/* ------------------------------------------------------------------ */
function RelChips({ relation, setRelation, disabled, onPick }) {
  return (
    <div className="ft-rel" role="radiogroup" aria-label="How do you know them?">
      {RELATIONS.map((r) => (
        <button
          key={r.value}
          type="button"
          role="radio"
          aria-checked={relation === r.value}
          className={`fr-chip-btn${relation === r.value ? ' is-on' : ''}`}
          onClick={() => { setRelation(relation === r.value ? '' : r.value); onPick?.(); }}
          disabled={disabled}
        >
          <span aria-hidden="true">{r.icon}</span>{r.label}
        </button>
      ))}
    </div>
  );
}

function SendButton({ relation, sending, sent, onSend }) {
  return (
    <button
      type="button"
      className={`fr-btn ${sent ? 'fr-btn--ok' : 'fr-btn--primary'}`}
      onClick={onSend}
      disabled={!relation || sending || sent}
    >
      {sent ? <><CheckRoundedIcon /> Request sent</> : sending ? 'Sending…' : relation ? 'Send request' : 'Pick a relation'}
    </button>
  );
}

/** Rich profile preview shown while hovering a tile */
function ProfileHoverCard({ user, anchor, relation, setRelation, sending, sent, onSend, onPick, profileHref, onEnter, onLeave }) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const gap = 12;
    let left = anchor.right + gap;
    if (left + w > vw - 8) left = anchor.left - w - gap;
    if (left < 8) left = Math.max(8, Math.min(vw - w - 8, anchor.left));
    const top = Math.max(8, Math.min(anchor.top + anchor.height / 2 - h / 2, vh - h - 8));
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
    el.style.visibility = 'visible';
  }, [anchor, user]);

  const name = userName(user);
  const followers = user.followersCount ?? user.followers ?? 0;
  const skills = normalizeSkills(user.skills);
  const links = normalizeLinks(user.linkLabels);
  const bio = user.bio || user.shortInfo || '';
  const joined = user.created && !Number.isNaN(new Date(user.created).getTime())
    ? new Date(user.created).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
    : '';
  const coverUrls = fileUrlCandidates(user, user.cover);
  const h = hueOf(name);

  const card = (
    <div ref={ref} className="fh" role="dialog" aria-label={`${name} profile preview`} onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <div className="fh-body">
        <div className="fh-left">
          <FriendAvatar user={user} size={96} radius="26px" />
          <div>
            <strong>{followers}</strong>
            <span> {Number(followers) === 1 ? 'follower' : 'followers'}</span>
          </div>
        </div>

        <div className="fh-right">
          <div
            className="fh-cover"
            style={coverUrls.length ? undefined : { background: `linear-gradient(135deg,hsl(${h},60%,38%),hsl(${(h + 60) % 360},65%,30%))` }}
          >
            {coverUrls.length > 0 && (user.coverType === 'video'
              ? <video src={coverUrls[0]} autoPlay loop muted playsInline />
              : <FileImg urls={coverUrls} />)}
          </div>

          <div className="fh-details">
            <h3 className="fh-name">{name}</h3>
            {user.username && <span className="fh-handle">@{user.username}</span>}
            <div className="fh-meta">
              {user.profession && <span><WorkOutlineRoundedIcon />{user.profession}</span>}
              {user.city && <span><LocationOnRoundedIcon />{user.city}</span>}
              {joined && <span><CalendarMonthRoundedIcon />Joined {joined}</span>}
              {user.peerId && <span>ID: {user.peerId}</span>}
            </div>
            {bio && <p className="fh-bio">{bio}</p>}
            {skills.length > 0 && (
              <div className="ft-skills">
                {skills.map((s, i) => {
                  const sh = hueOf(s);
                  return (
                    <span key={`${s}-${i}`} className="ft-skill" style={{ background: `hsla(${sh},70%,55%,.16)`, border: `1px solid hsla(${sh},70%,55%,.38)` }}>{s}</span>
                  );
                })}
              </div>
            )}
            {links.length > 0 && (
              <div className="fh-links">
                {links.map((l, i) => (
                  <a key={`${l.href}-${i}`} className="fh-link" href={l.href} target="_blank" rel="noopener noreferrer"><LinkRoundedIcon />{l.label}</a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="fh-foot">
        <div className="fh-req">
          <RelChips relation={relation} setRelation={setRelation} disabled={sent} onPick={onPick} />
          <SendButton relation={relation} sending={sending} sent={sent} onSend={onSend} />
        </div>
        <a className="fr-btn fh-go" href={profileHref} target="_blank" rel="noopener noreferrer">
          Go to Profile <OpenInNewRoundedIcon />
        </a>
      </div>
    </div>
  );

  return createPortal(card, document.querySelector('.mf-feed') || document.body);
}

/* ------------------------------------------------------------------ */
/* FriendTile                                                          */
/* ------------------------------------------------------------------ */
/**
 * Props
 * - userRecord, currentUserId
 * - onSendRequest(toUserId, relationType) -> Promise   (FriendsPanel supplies this)
 * - onBlock(userId)                                    (FriendsPanel supplies this)
 * - onRemove(userId)   called after the "hide suggestion" animation
 * - onRequestSent(record)  optional, legacy callback
 * - hoverCard (default true): show the profile preview card on mouse hover
 */
export default function FriendTile({
  userRecord,
  currentUserId,
  onSendRequest,
  onBlock,
  onRemove,
  onRequestSent,
  compact = false,
  hoverCard = true,
}) {
  const navigate = useNavigate();
  const profileHref = useHref(`/profile/${userRecord?.id ?? ''}`);
  const [relation, setRelation] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [dx, setDx] = useState(0);
  const [anchor, setAnchor] = useState(null);
  const [canHover] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches,
  );
  const touch = useRef(null);
  const tileRef = useRef(null);
  const showT = useRef(null);
  const hideT = useRef(null);

  useEffect(() => () => { clearTimeout(showT.current); clearTimeout(hideT.current); }, []);

  // close the preview when anything scrolls underneath it
  useEffect(() => {
    if (!anchor) return undefined;
    const close = () => setAnchor(null);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [anchor]);

  if (!userRecord || hidden) return null;

  const name = userName(userRecord);
  const followers = userRecord.followersCount ?? userRecord.followers ?? 0;
  const city = userRecord.city || '';
  const handle = userRecord.username ? `@${userRecord.username}` : '';
  const bio = userRecord.shortInfo || userRecord.bio || '';
  const skills = normalizeSkills(userRecord.skills);
  const subline = [handle, city].filter(Boolean).join(' · ');

  const openProfile = () => navigate(`/profile/${userRecord.id}`);

  /* hover preview */
  const enterTile = () => {
    if (!canHover || !hoverCard || leaving) return;
    clearTimeout(hideT.current);
    if (anchor) return;
    clearTimeout(showT.current);
    showT.current = setTimeout(() => {
      const r = tileRef.current?.getBoundingClientRect();
      if (r) setAnchor({ left: r.left, right: r.right, top: r.top, bottom: r.bottom, height: r.height });
    }, 260);
  };
  const leaveTile = () => {
    clearTimeout(showT.current);
    clearTimeout(hideT.current);
    hideT.current = setTimeout(() => setAnchor(null), 180);
  };
  const keepCard = () => clearTimeout(hideT.current);

  const dismiss = () => {
    if (leaving) return;
    setAnchor(null);
    setLeaving(true);
    setTimeout(() => {
      if (onRemove) onRemove(userRecord.id);
      else setHidden(true);
    }, 180);
  };

  const sendRequest = async () => {
    if (!relation || sending || sent) return;
    setSending(true);
    try {
      let created;
      if (onSendRequest) {
        created = await onSendRequest(userRecord.id, relation);
      } else {
        if (!currentUserId) return;
        created = await pb.collection('relation_requests').create({
          fromUser: currentUserId,
          toUser: userRecord.id,
          relationType: relation,
          status: 'pending',
          createdAt: new Date().toISOString(),
        });
      }
      onRequestSent?.(created);
      setSent(true);
      setRelation('');
    } catch (err) {
      console.error('Failed to send relation request', err);
    } finally {
      setSending(false);
    }
  };

  const block = async () => {
    setAnchor(null);
    if (onBlock) {
      onBlock(userRecord.id);
      return;
    }
    if (!currentUserId || !window.confirm(`Block ${name}?`)) return;
    try {
      await pb.collection('relation_requests').create({
        fromUser: currentUserId,
        toUser: userRecord.id,
        relationType: 'block',
        status: 'blocked',
        createdAt: new Date().toISOString(),
      });
      setHidden(true);
    } catch (err) {
      console.error('Block failed', err);
    }
  };

  /* swipe sideways to hide (vertical scrolling stays untouched) */
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touch.current = { x: t.clientX, y: t.clientY, locked: false };
  };
  const onTouchMove = (e) => {
    const s = touch.current;
    if (!s) return;
    const t = e.touches[0];
    const ddx = t.clientX - s.x;
    const ddy = t.clientY - s.y;
    if (!s.locked) {
      if (Math.abs(ddx) > 10 && Math.abs(ddx) > Math.abs(ddy)) s.locked = true;
      else return;
    }
    setDx(ddx);
  };
  const onTouchEnd = () => {
    const s = touch.current;
    touch.current = null;
    if (s?.locked && Math.abs(dx) > 110) {
      dismiss();
      return;
    }
    setDx(0);
  };

  return (
    <>
      <article
        ref={tileRef}
        className={`ft${compact ? ' ft--compact' : ''}${leaving ? ' is-leaving' : ''}${dx ? ' is-drag' : ''}`}
        style={dx ? { transform: `translateX(${dx}px)`, opacity: Math.max(0.35, 1 - Math.abs(dx) / 260) } : undefined}
        onMouseEnter={enterTile}
        onMouseLeave={leaveTile}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <button type="button" className="ft-x" aria-label={`Hide ${name}`} title="Not interested" onClick={dismiss}>
          <CloseRoundedIcon />
        </button>

        <button type="button" className="ft-id" onClick={openProfile} aria-label={`Open ${name}'s profile`}>
          <FriendAvatar user={userRecord} size={compact ? 46 : 52} />
          <span className="ft-meta">
            <span className="ft-name">{name}</span>
            {subline && <span className="ft-sub">{subline}</span>}
            <span className="ft-stat">{followers} {Number(followers) === 1 ? 'follower' : 'followers'}</span>
          </span>
        </button>

        {bio && <p className="ft-bio">{bio}</p>}

        {skills.length > 0 && (
          <div className="ft-skills">
            {skills.slice(0, 4).map((skill, i) => {
              const h = hueOf(skill);
              return (
                <span
                  key={`${skill}-${i}`}
                  className="ft-skill"
                  style={{ background: `hsla(${h},70%,55%,.16)`, border: `1px solid hsla(${h},70%,55%,.38)` }}
                >
                  {skill}
                </span>
              );
            })}
            {skills.length > 4 && <span className="ft-skill ft-skill--more">+{skills.length - 4}</span>}
          </div>
        )}

        <div className="ft-actions">
          <RelChips relation={relation} setRelation={setRelation} disabled={sent} onPick={() => setSent(false)} />
          <div className="ft-btns">
            <SendButton relation={relation} sending={sending} sent={sent} onSend={sendRequest} />
            <button
              type="button"
              className="fr-btn fr-btn--icon fr-btn--danger"
              onClick={block}
              aria-label={`Block ${name}`}
              title="Block"
            >
              <BlockRoundedIcon />
            </button>
          </div>
        </div>
      </article>

      {anchor && (
        <ProfileHoverCard
          user={userRecord}
          anchor={anchor}
          relation={relation}
          setRelation={setRelation}
          sending={sending}
          sent={sent}
          onSend={sendRequest}
          onPick={() => setSent(false)}
          profileHref={profileHref}
          onEnter={keepCard}
          onLeave={leaveTile}
        />
      )}
    </>
  );
}